import { memo } from 'react';

import { cn } from '@infinex/ui-styles';
import { arePropsEqual } from '@infinex/web-shared/utils';

import { FormattedNumber } from './FormattedNumber';

/**
 * 💰 A specialized number formatting component for prices.
 *
 * Handles price formatting with specific rules:
 * - For prices >= 1: Shows 2 decimal places
 * - For prices < 1: Shows 3 significant figures
 * - Supports zero-index notation for very small numbers
 * - Optional prefix display ($ and/or +/- sign)
 *
 * @example
 * // Basic price
 * <FormattedPrice value={123.456} /> // "123.46"
 *
 * // With dollar sign
 * <FormattedPrice value={123.456} prefixDollarSign /> // "$123.46"
 *
 * // With sign (always shows + or -)
 * <FormattedPrice value={123.456} prefixSign /> // "+123.46"
 * <FormattedPrice value={-123.456} prefixSign /> // "-123.46"
 *
 * // With both
 * <FormattedPrice value={123.456} prefixDollarSign prefixSign /> // "+$123.46"
 *
 * // Small numbers
 * <FormattedPrice value={0.00123} prefixDollarSign /> // "$0.00123"
 *
 * // Very small numbers (zero-index notation)
 * <FormattedPrice value={0.000000789} prefixDollarSign /> // "$0.0[5]789"
 */
interface FormattedPriceProps {
  /** The price value to format */
  value: number;
  /** Whether to show the dollar sign ($) prefix */
  prefixDollarSign?: boolean;
  /** Whether to show the sign (+ or -) prefix. When true, always shows either + or - */
  prefixSign?: boolean;

  className?: string;
  withAnimation?: boolean;
  obfuscate?: boolean;
  obfuscationOptions?: {
    length?: number;
    variant?: 'asterisk' | 'remove';
  };
  strikeThrough?: boolean;
}

export const FormattedPrice = memo(FormattedPriceComponent, arePropsEqual);

function FormattedPriceComponent({
  value,
  prefixDollarSign = false,
  prefixSign = false,
  className,
  withAnimation = false,
  obfuscate = false,
  obfuscationOptions = {
    length: 5,
    variant: 'asterisk',
  },
  strikeThrough,
}: FormattedPriceProps) {
  const isNegative = value < 0;
  const absValue = Math.abs(value);

  let prefix = '';
  if (prefixSign) {
    prefix += isNegative ? '-' : '+';
  }
  if (prefixDollarSign) {
    prefix += '$';
  }

  return (
    <span className={cn(className)}>
      <FormattedNumber
        value={absValue}
        obfuscate={obfuscate}
        obfuscationOptions={obfuscationOptions}
        withAnimation={withAnimation}
        prefix={prefix}
        strikeThrough={strikeThrough}
        // 0.95 so that it doesn't flicker when hovering over a stablecoin that
        // bounces between 0.99995 and 1.00005 etc. Need some reasonable margin of error.
        {...(absValue <= 0.95 && absValue !== 0
          ? { significantFigures: 3 }
          : { decimals: 2 })}
      />
    </span>
  );
}
